import { trackEvent } from "@entrilia/shared/logging";
import { sanitizeFileSrcForTelemetry } from "@entrilia/shared/utilities/urlTelemetry";
import { resolveClientCode } from "../../auth/accessCheck/accessCheckClientCode";
import type { DocumentFailureOrigin } from "../../auth/accessCheck/judgeDocumentFailure";
import type { BucketClassification, ConnectionCheckResult, DocumentLoadFailure } from "../../auth/accessCheck/types";
import { pdfBlobCheckEvents } from "../../trackingEventNames";
import type { PdfRetrySessionPhase } from "./pdfBlobCheckTypes";

interface BlobProbeProperties {
  allProbeHosts: string;
  failedProbeHosts: string;
  probeFailuresCount: number;
}

const buildBlobProbeProperties = (checks: ConnectionCheckResult[]): BlobProbeProperties => {
  const blobChecks = checks.filter((check) => check.id === "blob");
  const failed = blobChecks.filter((check) => !check.ok);
  return {
    allProbeHosts: blobChecks.map((check) => check.targetHost).join(","),
    failedProbeHosts: failed.map((check) => check.targetHost).join(","),
    probeFailuresCount: failed.length,
  };
};

interface BaseProps {
  fileSrc: string;
  documentViewSessionId?: string;
}

const baseProperties = ({ fileSrc, documentViewSessionId }: BaseProps) => ({
  fileSrc: sanitizeFileSrcForTelemetry(fileSrc),
  ...(documentViewSessionId ? { documentViewSessionId } : {}),
  clientCode: resolveClientCode() ?? "",
});

export const emitPdfBlobCheckTriggered = (props: BaseProps): void => {
  trackEvent(pdfBlobCheckEvents.PDF_LOAD_ERROR, baseProperties(props));
};

export type RetryOutcomeKind = "retried-ok" | "refreshed-ok" | "exhausted" | "cancelled" | "error";
export type RetryAbortReason = "document-changed-or-unmounted" | "results-closed";

interface RetryTelemetry {
  retryAttempts: number;
  retryOutcome: RetryOutcomeKind;
}

interface CompletedProps extends BaseProps {
  classification: BucketClassification;
  checks: ConnectionCheckResult[];
  documentFailureOrigin?: DocumentFailureOrigin;
  loadFailure?: DocumentLoadFailure;
  retry?: RetryTelemetry;
}

export const emitPdfBlobCheckCompleted = ({
  classification,
  checks,
  documentFailureOrigin,
  loadFailure,
  retry,
  ...base
}: CompletedProps): void => {
  trackEvent(pdfBlobCheckEvents.COMPLETED, {
    ...baseProperties(base),
    bucket: classification.bucket,
    ...buildBlobProbeProperties(checks),
    ...(documentFailureOrigin === undefined
      ? {}
      : { documentFailureOrigin, azureAuthored: documentFailureOrigin === "azure-authored" }),
    ...(loadFailure === undefined
      ? {}
      : {
          pdfErrorName: loadFailure.errorName,
          ...(loadFailure.statusCode === undefined ? {} : { pdfErrorStatus: loadFailure.statusCode }),
        }),
    ...(retry ?? {}),
  });
};

export const emitPdfBlobCheckFailed = (props: BaseProps): void => {
  trackEvent(pdfBlobCheckEvents.FAILED, baseProperties(props));
};

interface RetryAttemptProps extends BaseProps {
  attempt: number;
  maxAttempts: number;
  urlRefreshed: boolean;
}

export const emitPdfRetryAttempt = ({ attempt, maxAttempts, urlRefreshed, ...base }: RetryAttemptProps): void => {
  trackEvent(pdfBlobCheckEvents.RETRY_ATTEMPT, { ...baseProperties(base), attempt, maxAttempts, urlRefreshed });
};

interface RetryAbortedProps extends BaseProps {
  attempts: number;
  maxAttempts: number;
  phase: PdfRetrySessionPhase;
  reason: RetryAbortReason;
  urlRefreshed: boolean;
}

export const emitPdfRetryAborted = ({
  attempts,
  maxAttempts,
  phase,
  reason,
  urlRefreshed,
  ...base
}: RetryAbortedProps): void => {
  trackEvent(pdfBlobCheckEvents.RETRY_ABORTED, {
    ...baseProperties(base),
    attempts,
    maxAttempts,
    phase,
    reason,
    urlRefreshed,
  });
};

interface RetryResolvedProps extends BaseProps {
  attempts: number;
  outcome: RetryOutcomeKind;
  sasExpired: boolean;
  urlRefreshed: boolean;
}

export const emitPdfRetryResolved = ({
  attempts,
  outcome,
  sasExpired,
  urlRefreshed,
  ...base
}: RetryResolvedProps): void => {
  trackEvent(pdfBlobCheckEvents.RETRY_RESOLVED, {
    ...baseProperties(base),
    attempts,
    outcome,
    sasExpired,
    urlRefreshed,
  });
};
